import BaseLinkOrButton from "../../../../../common/core-ui/BaseLinkOrButton.tsx";
import { clsx } from "clsx";
import { FormattedMessage } from "react-intl";
import styles from "./MarketplaceOptions.module.scss";
import MiscellaneousVerifiedSeller from "../../../../../common/icons/MiscellaneousVerifiedSeller.tsx";
import MiscellaneousExternalWindow from "../../../../../common/icons/MiscellaneousExternalWindow.tsx";
import useMarketplaceOptions from "./useMarketplaceOptions.ts";
import CenteredDotLoader from "../../../../../common/core-ui/CenteredDotLoader.tsx";
import ErrorBoundary from "../../../../../common/core-ui/ErrorBoundary.tsx";
import MarketplaceRadioButton from "./MarketplaceRadioButton.tsx";
import { useFetcher } from "react-router-dom";
import LoaderButton from "../../../../../common/core-ui/LoaderButton.tsx";
import { useEffect } from "react";
import { useMarketplaceFetcher } from "../../../product/marketplaceLoader.ts";
import { PrintVariant } from "../../../../../common/api/decimal/methods/product.ts";

function MarketplaceFormElements({
  fetcher,
  variantId,
  disabled,
}: {
  fetcher: ReturnType<typeof useMarketplaceFetcher>;
  variantId: number;
  disabled: boolean;
}) {
  if (!fetcher.data) {
    return <CenteredDotLoader />;
  }

  if (fetcher.data.data!.length === 0) {
    return (
      <div className={styles.noItems}>
        <FormattedMessage
          defaultMessage="Item No Longer Available"
          tagName="h3"
        />
        <FormattedMessage
          defaultMessage="This item is no longer available for purchase. We are sorry for the inconvenience."
          tagName="p"
        />
      </div>
    );
  }

  return (
    <>
      {fetcher.data.data!.map((item, i) => (
        <MarketplaceRadioButton
          item={item}
          key={item.productId}
          defaultChecked={i === 0}
          disabled={disabled}
        />
      ))}
      <input name="quantity" type="hidden" value={1} />
      <input name="variantId" type="hidden" value={variantId} />
    </>
  );
}

export default function MarketplaceOptions({
  onRequestClose,
  variant,
  vbid,
}: {
  onRequestClose: () => void;
  variant: PrintVariant;
  vbid?: string;
}) {
  const optionsFetcher = useMarketplaceOptions(variant.originalSku, vbid);
  const cartFetcher = useFetcher();
  const isProcessing = cartFetcher.state !== "idle";
  // presence of vbid means called from another route
  const action = vbid ? `products/${vbid}` : undefined;

  useEffect(() => {
    if (cartFetcher.state === "idle" && cartFetcher.data) {
      onRequestClose();
    }
  }, [onRequestClose, cartFetcher]);

  return (
    <cartFetcher.Form className={styles.contents} action={action} method="POST">
      <h2 className={styles.header}>
        <FormattedMessage defaultMessage="Marketplace Options" />
      </h2>
      <div className={styles.sellerBox}>
        <MiscellaneousVerifiedSeller />
        <div>
          <FormattedMessage
            tagName="div"
            defaultMessage="All sellers are verified and vetted for quality. Marketplace items are <strong>not returnable.</strong>"
            values={{ strong: (chunks) => <strong>{chunks}</strong> }}
          />
          <BaseLinkOrButton
            className={"vst-link"}
            href="https://textbookx.zendesk.com/hc/en-us/articles/28515881387287"
          >
            <FormattedMessage defaultMessage="View all return policies" />
            <MiscellaneousExternalWindow />
          </BaseLinkOrButton>
        </div>
      </div>
      <ErrorBoundary>
        <MarketplaceFormElements
          fetcher={optionsFetcher}
          variantId={variant.id}
          disabled={isProcessing}
        />
      </ErrorBoundary>
      {optionsFetcher.data && optionsFetcher.data.data!.length > 0 && (
        <div className={styles.footer}>
          <BaseLinkOrButton
            onClick={onRequestClose}
            className={clsx("vst-button-pill", "vst-button-variant-naked")}
          >
            <FormattedMessage defaultMessage="Cancel" />
          </BaseLinkOrButton>
          <LoaderButton
            className={clsx("vst-button-pill")}
            type="submit"
            loading={isProcessing}
          >
            <FormattedMessage defaultMessage="Add to Cart" />
          </LoaderButton>
        </div>
      )}
    </cartFetcher.Form>
  );
}
